import { Button } from 'primereact/button';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { ExitSlipSnapshotMeterTemplate } from './ExitSlipSnapshotMeterTemplate';
import { MeterGroup } from 'primereact/metergroup';
import { MeterOptionExitSlip } from '../../../models/MeterOption';
import {
  ClassExitSlip,
  FullDetailModalData,
  LearnosityReportData,
  useExitSlipChartData,
} from '../../../custom-hooks/useExitSlipChartValues';
import { getFullDetailModalData, getUnfinishedStudents } from '../../../businessLogicService/exitSlipSnapshotHelper';
import SupportIcon from './ExitSlipIcons/SupportIcon';
import QuestionIcon from './ExitSlipIcons/QuestionIcon';
import { PrintModalData } from '../../ExitSlip/ExitSlipModalBodyData';
import { splitId } from '../../../businessLogicService/splitIdsFromBackEnd';
import { format } from 'date-fns';
import { SkillModalData } from './ExitSlipSnapshot';
import { ArrowRight, CircleMinus } from 'lucide-react';
import ExitSlipUnfinishedStudent from './ExitSlipUnfinishedStudent';
import ExitSlipSnapshotEditAssignmentMenu from './ExitSlipSnapshotEditAssignmentMenu';

interface Props {
  exitSlipValues: ClassExitSlip;
  setFullDetailModalData: Dispatch<SetStateAction<FullDetailModalData>>;
  setSkillModalData: Dispatch<SetStateAction<SkillModalData>>;
  setPrintModalData: Dispatch<SetStateAction<PrintModalData>>;
  reportData: LearnosityReportData;
  setReportData: Dispatch<SetStateAction<LearnosityReportData>>;
  dateExitSlip: Date;
  canEditAssignment: boolean;
  editAssignmentDisabled: boolean;
  onReschedule: () => void;
  onCancelAssignment: () => void;
}

export interface UserClass {
  id: string;
  lastName: string | null | undefined;
  fullName: string | null | undefined;
  publicUserId: string;
  roles: {
    id: string;
    name: string | null | undefined;
  }[];
}

const ExitSlipSnapshotCardBody: FC<Props> = ({
  exitSlipValues,
  setFullDetailModalData,
  setSkillModalData,
  setPrintModalData,
  reportData,
  setReportData,
  dateExitSlip,
  canEditAssignment,
  editAssignmentDisabled,
  onReschedule,
  onCancelAssignment,
}) => {
  const valuesForChart = useExitSlipChartData(exitSlipValues);
  const [meterKey, setMeterKey] = useState<number>(0);

  const canceled = !!exitSlipValues.canceledOn;
  const respondedCount = exitSlipValues.learnosityExitSlipSessions.length;
  //getUnfinishedStudents mutates the Date it is handed, so it never sees the prop itself
  const rosterCount = canceled
    ? respondedCount + getUnfinishedStudents(exitSlipValues, new Date(dateExitSlip)).length
    : 0;

  const handleReportModalButton = () => {
    const data = getFullDetailModalData(exitSlipValues);
    setReportData({ ...reportData, userIds: data.usersIds, sessionIds: data.sessionIds });
    setFullDetailModalData({
      title: `Lesson ${exitSlipValues.curriculumLesson.number}: ${exitSlipValues.curriculumLesson.name}`,
      visible: true,
    });
  };

  const handlePrintModalButton = () => {
    setPrintModalData({
      date: format(dateExitSlip, 'MM/dd/yyyy'),
      title: `Lesson ${exitSlipValues.curriculumLesson.number}: ${exitSlipValues.curriculumLesson.name as string}`,
      activityId: `ExitSlip_Miami_${splitId(exitSlipValues.curriculumLesson.id as string)}_${
        exitSlipValues.curriculumLesson.skill.code
      }`,
      visible: true,
      fullScreenButton: true,
    });
  };

  const handleSkillModalButton = () => {
    setSkillModalData({
      visible: true,
      skill: {
        id: exitSlipValues.curriculumLesson.skill.id,
        code: exitSlipValues.curriculumLesson.skill.code as string,
        name: exitSlipValues.curriculumLesson.skill.name as string,
      },
    });
  };

  useEffect(() => {
    setMeterKey(new Date().getTime());
  }, [valuesForChart]);

  return (
    <>
      <div className="col-12 nc_exit-slip-snapshot-with-data tw-overflow-hidden">
        {canceled && (
          <div className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-t-[20px] tw-bg-neutral-800 tw-px-6 tw-py-2">
            <CircleMinus size={14} strokeWidth={2} className="tw-shrink-0 tw-text-white" />
            <span className="tw-font-atkinson tw-text-sm tw-font-bold tw-leading-5 tw-text-white">Canceled</span>
          </div>
        )}
        <div className="tw-flex tw-w-full tw-flex-1 tw-items-stretch">
          <div
            className={`nc_exit-slip-snapshot-with-data-left tw-flex tw-flex-col ${
              canceled
                ? 'tw-w-[346px] tw-shrink-0 tw-gap-4 !tw-border-y-0 !tw-border-l-0 !tw-border-r-2 !tw-border-dashed !tw-border-[#E1E1E1] !tw-py-4 !tw-pl-6 !tw-pr-4 !tw-rounded-none'
                : 'md:col-5 tw-justify-between'
            }`}
          >
            <div className={`tw-flex tw-flex-col ${canceled ? 'tw-gap-1' : ''}`}>
              <div className={`tw-flex tw-flex-row tw-justify-between tw-items-center ${canceled ? '' : 'tw-mb-2'}`}>
                <p
                  className={
                    canceled
                      ? 'tw-m-0 tw-font-atkinson tw-text-xs tw-font-bold tw-leading-4 tw-text-[#7C7C7C]'
                      : 'tw-w-max tw-rounded-lg tw-p-1 tw-text-xs tw-uppercase tw-font-atkinson tw-font-bold tw-text-stone-600 tw-mx-0 tw-my-1'
                  }
                >
                  {exitSlipValues.curriculumLesson.targetGroup.name}
                </p>
                <Button
                  className="nc_exit-slip-question-button"
                  icon={<QuestionIcon />}
                  tooltip="See questions"
                  tooltipOptions={{ position: 'top' }}
                  rounded
                  outlined
                  onClick={handlePrintModalButton}
                />
              </div>
              <h4
                className={
                  canceled
                    ? 'tw-m-0 tw-w-full tw-text-left tw-font-sora tw-text-base tw-font-bold tw-leading-6 tw-text-[#393939]'
                    : 'tw-text-left tw-w-full tw-font-bold'
                }
              >
                <span>Lesson {exitSlipValues.curriculumLesson.number}: </span>
                <span>{exitSlipValues.curriculumLesson.name}</span>
              </h4>
            </div>
            {canceled && (
              <p className="tw-m-0 tw-font-atkinson tw-text-sm tw-leading-5 tw-text-[#393939]">
                <span className="tw-font-bold">{`${respondedCount} of ${rosterCount} `}</span>
                students responded
              </p>
            )}
            <div className="nc_exit-slip-snapshot-related-skill">
              <p>Related skill:</p>
              <div className="xl:tw-w-max" onClick={handleSkillModalButton}>
                <SupportIcon />
                <p>
                  {exitSlipValues.curriculumLesson.skill.name} {' (' + exitSlipValues.curriculumLesson.skill.code + ')'}
                </p>
              </div>
            </div>
            {canEditAssignment && (
              <div className="tw-mt-3 tw-flex tw-justify-end tw-border-0 tw-border-t tw-border-solid tw-border-neutral-300 tw-pt-3">
                <ExitSlipSnapshotEditAssignmentMenu
                  disabled={editAssignmentDisabled}
                  onReschedule={onReschedule}
                  onCancel={onCancelAssignment}
                />
              </div>
            )}
          </div>
          {valuesForChart.length > 0 ? (
            <div
              className={`tw-group tw-flex tw-flex-col tw-items-center ${
                canceled ? 'tw-w-full tw-justify-center tw-rounded-lg tw-bg-[#F9F8F5] !tw-p-5' : 'md:col-7 tw-p-4'
              }`}
            >
              {!canceled && <ExitSlipUnfinishedStudent exitSlipValues={exitSlipValues} dateExitSlip={dateExitSlip} />}
              <div
                className={`tw-flex tw-flex-row tw-items-end tw-justify-center tw-gap-5 tw-px-5 ${
                  canceled ? '' : 'tw-mt-2'
                }`}
              >
                {valuesForChart.map((el, index) => {
                  return (
                    <div
                      key={index}
                      className="nc_metergroup-exit-slip tw-flex tw-w-20 tw-flex-col tw-items-center tw-gap-1 tw-px-2"
                    >
                      <p className="tw-my-0 tw-font-atkinson tw-text-sm tw-font-bold tw-leading-5 tw-text-[#686868]">
                        Q{el.questionNumber ? el.questionNumber : index + 1}
                      </p>
                      <MeterGroup
                        key={meterKey}
                        className="nc_metergroup-vertical"
                        values={el.values.filter(value => value.value > 0)}
                        orientation="vertical"
                        labelOrientation="vertical"
                        meter={met => (
                          <ExitSlipSnapshotMeterTemplate
                            element={met as MeterOptionExitSlip}
                            totalStudents={el.students}
                          />
                        )}
                      />
                    </div>
                  );
                })}
              </div>
              <div className={`tw-h-0.5 tw-w-[300px] tw-rounded-[20px] tw-bg-[#F1F0EE] ${canceled ? '' : 'tw-mt-4'}`} />
              {/* focus-within keeps it reachable by keyboard, where hover never fires */}
              {!canceled && (
                <div className="tw-mt-4 tw-self-end tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100 group-focus-within:tw-opacity-100">
                  <Button
                    label="See full details"
                    className="tw-gap-1 tw-flex-row-reverse"
                    icon={<ArrowRight size={14} />}
                    link
                    onClick={handleReportModalButton}
                  />
                </div>
              )}
            </div>
          ) : (
            <div
              className={`md:col-7 tw-flex tw-flex-col tw-items-center ${
                canceled ? 'tw-justify-center tw-rounded-lg tw-bg-[#F9F8F5] !tw-p-5' : 'tw-p-4 tw-justify-between'
              }`}
            >
              {!canceled && <ExitSlipUnfinishedStudent exitSlipValues={exitSlipValues} dateExitSlip={dateExitSlip} />}
              <div className="tw-flex tw-flex-row tw-justify-self-center">
                <p className="tw-font-bold tw-font-atkinson">No Data yet</p>
              </div>
              {!canceled && <div>{''}</div>}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ExitSlipSnapshotCardBody;
